/**
 * Detect when a parsed answer-markdown table is a simple numeric series that can
 * be drawn as a minimal chart (see {@link MiniChart}). Purely heuristic and
 * conservative: the goal is to light up the obvious "category/period → number"
 * tables the agent already emits, never to chart noisy/text-heavy tables.
 */
import {AnswerTable} from 'views/admin/answerTableCsv';

export type GraphSeries = {
  name: string;
  values: number[];
};

export type GraphableSeries = {
  /** ``line`` for time/period-ordered labels, ``bar`` for categorical labels. */
  kind: 'line' | 'bar';
  labelHeader: string;
  labels: string[];
  series: GraphSeries[];
};

/** A minimum that keeps a chart meaningful (two points is just a slope). */
const MIN_ROWS = 3;
/** A column counts as a series only if most of its cells are pure numbers. */
const MIN_NUMERIC_RATIO = 0.8;
/** A column is treated as a label (non-numeric dimension) below this ratio. */
const LABEL_MAX_NUMERIC_RATIO = 0.5;
/** Cap overlaid line series so the mini chart stays readable. */
const MAX_SERIES = 3;
/** Fraction of labels that must look temporal to pick a line (vs bar) chart. */
const TEMPORAL_LABEL_RATIO = 0.6;
/**
 * Max max/min ratio for a bar series. Bars across mixed units (e.g. revenue in
 * millions next to an NPS of 45) aren't comparable, and a span this wide is a
 * reliable tell — so we decline to chart it.
 */
const BAR_SPREAD_LIMIT = 1000;
/**
 * Headers whose numbers aren't quantities to plot (identifiers, ranks, years,
 * contact/level codes). Matched against series-column headers only — a ``year``
 * column is still fine as the x-axis label.
 */
const BLOCKED_SERIES_HEADER = /\b(id|ids|rank|ranking|zip|postal)\b/i;

/** Number token with an optional attached magnitude suffix (``5.2M`` → 5,200,000). */
const NUMERIC_TOKEN = /(-?\d[\d,]*(?:\.\d+)?)([kmbt])?(?![A-Za-z])/i;
const SUFFIX_FACTORS: Record<string, number> = {k: 1e3, m: 1e6, b: 1e9, t: 1e12};

/**
 * Parse a markdown cell to a number, tolerating ``$``, ``%``, thousands
 * separators, and an attached magnitude suffix (``k``/``m``/``b``/``t``). The
 * suffix must abut the number (so ``5 members`` stays 5, not 5,000,000). Returns
 * null when there's no leading numeric token.
 */
export const parseNumericCell = (cell: string): number | null => {
  const match = cell.trim().match(NUMERIC_TOKEN);
  if (!match) {
    return null;
  }
  const base = Number(match[1].replace(/,/g, ''));
  if (!Number.isFinite(base)) {
    return null;
  }
  const suffix = match[2]?.toLowerCase();
  return suffix ? base * SUFFIX_FACTORS[suffix] : base;
};

type CellKind = 'empty' | 'number' | 'date' | 'text';

/** Cells that read as dates/periods — kept out of "numeric" so they stay on the x-axis. */
const DATE_CELL_PATTERNS = [
  /^\d{4}-\d{1,2}(-\d{1,2})?$/, // 2024-06, 2024-06-01
  /^\d{1,2}\/\d{1,2}\/\d{2,4}$/, // 06/01/2024
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s*\d{0,4}$/i, // Jan, Jan 2024
  /^q[1-4]\b/i, // Q1 2024
  /^(week|wk)\s*\d+/i, // Week 12
];

const isDateLike = (value: string): boolean => DATE_CELL_PATTERNS.some((p) => p.test(value));

/**
 * Classify a cell so a series column can require *pure numbers* (≥80%), rather
 * than counting dates (``2024-06`` → 2024) as numeric. This is what filters out
 * mixed-type columns (date/string/number) from becoming a series.
 */
const classifyCell = (cell: string): CellKind => {
  const s = cell.trim();
  if (!s) {
    return 'empty';
  }
  if (isDateLike(s)) {
    return 'date';
  }
  return parseNumericCell(s) !== null ? 'number' : 'text';
};

const TEMPORAL_PATTERNS = [
  /^\d{4}-\d{1,2}(-\d{1,2})?$/, // 2024-06, 2024-06-01
  /^\d{4}$/, // 2024
  /^q[1-4]\b/i, // Q1, Q2 2024
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i, // month names
  /^(week|wk)\s*\d+/i, // Week 12
  /^(mon|tue|wed|thu|fri|sat|sun)/i, // weekdays
];

const looksTemporal = (labels: string[]): boolean => {
  const nonEmpty = labels.map((l) => l.trim()).filter(Boolean);
  if (nonEmpty.length === 0) {
    return false;
  }
  const hits = nonEmpty.filter((l) => TEMPORAL_PATTERNS.some((p) => p.test(l))).length;
  return hits / nonEmpty.length >= TEMPORAL_LABEL_RATIO;
};

/**
 * Sortable timestamp for a temporal label, or null when it can't be parsed.
 * Covers the shapes {@link TEMPORAL_PATTERNS} accepts: ISO ``YYYY-MM(-DD)``,
 * bare ``YYYY``, ``Q1 2024`` / ``2024 Q1``, and anything ``Date.parse`` handles
 * (e.g. ``Jan 2024``).
 */
const temporalSortKey = (label: string): number | null => {
  const s = label.trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, iso[3] ? Number(iso[3]) : 1).getTime();
  }
  if (/^\d{4}$/.test(s)) {
    return new Date(Number(s), 0, 1).getTime();
  }
  const qFirst = s.match(/q([1-4])\D*(\d{4})/i); // "Q1 2024"
  const qLast = s.match(/(\d{4})\D*q([1-4])/i); // "2024 Q1"
  if (qFirst || qLast) {
    const q = Number(qFirst ? qFirst[1] : qLast![2]);
    const year = Number(qFirst ? qFirst[2] : qLast![1]);
    return new Date(year, (q - 1) * 3, 1).getTime();
  }
  const parsed = Date.parse(s);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Carry the previous value across gaps so a chosen series stays a clean number[]. */
const fillForward = (values: (number | null)[]): number[] => {
  const out: number[] = [];
  let last = 0;
  for (const value of values) {
    if (value === null) {
      out.push(last);
    } else {
      out.push(value);
      last = value;
    }
  }
  return out;
};

/**
 * Interpret a table as a chartable series, or return null when it doesn't look
 * like one. The first mostly-non-numeric column is the label (x) axis; remaining
 * mostly-numeric columns become series. Bar charts keep a single series so the
 * tiny rendering stays legible.
 */
export const toGraphableSeries = (table: AnswerTable): GraphableSeries | null => {
  const {headers, rows} = table;
  if (headers.length < 2 || rows.length < MIN_ROWS) {
    return null;
  }

  const colCount = headers.length;
  const kindsByCol: CellKind[][] = [];
  const parsedByCol: (number | null)[][] = [];
  for (let c = 0; c < colCount; c += 1) {
    const cells = rows.map((row) => row[c] ?? '');
    kindsByCol.push(cells.map(classifyCell));
    parsedByCol.push(cells.map(parseNumericCell));
  }
  // "Numeric" means *pure number* (dates/text don't count), so a date column is
  // never mistaken for a series and mixed-type columns fall below the threshold.
  const numericRatio = (c: number): number =>
    kindsByCol[c].filter((k) => k === 'number').length / rows.length;

  // A clean "category/period → number" table has a single dimension column (the
  // label) and every other column numeric. More than one non-numeric column means
  // this is a detail roster (e.g. workstream + status + owner + last update), not
  // an aggregation — charting those produces misleading bars, so decline.
  const nonNumericCols: number[] = [];
  for (let c = 0; c < colCount; c += 1) {
    if (numericRatio(c) < MIN_NUMERIC_RATIO) {
      nonNumericCols.push(c);
    }
  }
  if (nonNumericCols.length > 1) {
    return null;
  }

  // Label column: the first mostly-non-number column; fall back to column 0 (e.g.
  // a "year → value" table whose label column is itself numeric).
  let labelCol = 0;
  for (let c = 0; c < colCount; c += 1) {
    if (numericRatio(c) < LABEL_MAX_NUMERIC_RATIO) {
      labelCol = c;
      break;
    }
  }

  const numericCols: number[] = [];
  for (let c = 0; c < colCount; c += 1) {
    if (
      c !== labelCol &&
      numericRatio(c) >= MIN_NUMERIC_RATIO &&
      !BLOCKED_SERIES_HEADER.test(headers[c])
    ) {
      numericCols.push(c);
    }
  }
  if (numericCols.length === 0) {
    return null;
  }

  const labels = rows.map((row) => (row[labelCol] ?? '').trim());
  const kind = looksTemporal(labels) ? 'line' : 'bar';

  // A bar chart is a breakdown by category, so each category should appear once.
  // Duplicated labels (e.g. a list of rows that all share the same ``off_track``
  // status) mean the table isn't aggregated by the label column, so the bars
  // double-count categories and mislead — decline rather than chart noise.
  if (kind === 'bar') {
    const nonEmpty = labels.filter(Boolean);
    const distinct = new Set(nonEmpty).size;
    if (distinct < 2 || distinct < nonEmpty.length) {
      return null;
    }
  }

  const chosen = kind === 'bar' ? numericCols.slice(0, 1) : numericCols.slice(0, MAX_SERIES);
  const series = chosen.map((c) => ({name: headers[c], values: fillForward(parsedByCol[c])}));

  // Bars across mixed units (e.g. "Revenue 5.2M / Headcount 200 / NPS 45") aren't
  // comparable; a span beyond a few orders of magnitude is the tell, so skip it.
  if (kind === 'bar') {
    const positives = series[0].values.filter((v) => v > 0);
    if (
      positives.length > 0 &&
      Math.max(...positives) / Math.min(...positives) > BAR_SPREAD_LIMIT
    ) {
      return null;
    }
  }

  // A "… over time" table is usually sorted by value, not chronologically, so a
  // line drawn in row order is misleading. Reorder a line chart by its parsed
  // date when every label parses; otherwise keep the table's order untouched.
  if (kind === 'line') {
    const keys = labels.map(temporalSortKey);
    if (keys.every((k) => k !== null)) {
      const order = labels.map((_, i) => i).sort((a, b) => keys[a]! - keys[b]!);
      return {
        kind,
        labelHeader: headers[labelCol],
        labels: order.map((i) => labels[i]),
        series: series.map((s) => ({name: s.name, values: order.map((i) => s.values[i])})),
      };
    }
  }

  return {kind, labelHeader: headers[labelCol], labels, series};
};
